import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { useQueryState } from 'react-router-use-location-state';
import { InternalGrid } from '@paygreen/paygreen-ui';
import { useAPIError, useMode } from '../../context';
import { formatDateApi, formatCentsMoney, IsDesktop } from '../../utils/tools';
import { startOfMonth, endOfMonth } from '../../utils/constants';
import {
    H1Title,
    ErrorBoundary,
    NoDataFoundMessage,
    ToggleMode,
} from '../../components';
import { getAllPartnerships } from '../partnerships/service';
import DashboardDataCards from './DashboardDataCards';
import DashboardFilter from './DashboardFilter';
import DashboardCardHistogram from './DashboardCardHistogram';
import { getDonationReports } from './service';

const ShopDashboard = ({ t }) => {
    const { addError } = useAPIError();

    const [reportsData, setReportsData] = useState();
    const [histogramData, setHistogramData] = useState();
    const [partnershipsData, setPartnershipsData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState();

    const [startDate, setStartDate] = useQueryState('startDate', startOfMonth);
    const [endDate, setEndDate] = useQueryState('endDate', endOfMonth);
    const [partner, setPartner] = useState({});

    const { mode } = useMode();

    useEffect(() => {
        let isMounted = true;

        if (isMounted) {
            setIsLoading(true);
            setNoDataMessage();
        }

        const params = {
            begin: formatDateApi(startDate),
            end: formatDateApi(endDate),
            association: partner.id || null,
        };

        const fetchGlobalData = getDonationReports(params, mode)
            .then((res) => {
                if (res.data.donationCount === 0) {
                    isMounted && setNoDataMessage(t('dashboard.noDataFound'));
                } else {
                    return res.data;
                }
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
            });

        const fetchHistogramData = getDonationReports(
            { ...params, daily: 1 },
            mode,
        )
            .then((res) => {
                if (res.data.donationCount === 0) {
                    isMounted && setNoDataMessage(t('dashboard.noDataFound'));
                } else {
                    return res.data.daily;
                }
            })
            .catch((error) => {
                const { detail, status } = error?.response?.data;

                addError(detail, status);
            });

        Promise.all([fetchGlobalData, fetchHistogramData]).then((res) => {
            if (isMounted) {
                setReportsData(res[0] || null);

                setHistogramData(res[1] || null);
                setIsLoading(false);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [startDate, endDate, addError, partner.id, t, mode]);

    useEffect(() => {
        let isMounted = true;

        getAllPartnerships(mode)
            .then((res) => {
                if (isMounted) {
                    // we keep only associations from partnerships that are or have been active at some point
                    setPartnershipsData(
                        res.data._embedded.partnership.filter(
                            ({ associationStatus }) => {
                                return (
                                    associationStatus !== 'REFUSE' &&
                                    associationStatus !== 'WAITING'
                                );
                            },
                        ),
                    );
                }
            })
            .catch((error) => {
                if (error?.response?.data.status === 404) {
                    isMounted && setPartnershipsData([]);
                } else {
                    const { detail, status } = error?.response?.data;

                    addError(detail, status);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [addError, mode]);

    return (
        <>
            <Helmet>
                <title>
                    {t('dashboard.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('dashboard.title')}>
                <ToggleMode />
            </H1Title>

            <InternalGrid
                displayType="grid"
                childrenMarginBottom="lg"
                justifyItems="stretch"
                alignItems="stretch"
                gridTemplateColumns="100%"
                hasStaticWidth={IsDesktop() ? false : true}
            >
                <InternalGrid
                    displayType="grid"
                    childrenMarginBottom={noDataMessage ? 'md' : null}
                    justifyItems="stretch"
                    alignItems="stretch"
                    gridTemplateColumns="1fr"
                    hasStaticWidth={IsDesktop() ? false : true}
                >
                    <DashboardFilter
                        partnershipsData={partnershipsData}
                        setEndDate={setEndDate}
                        setStartDate={setStartDate}
                        startDate={startDate}
                        endDate={endDate}
                        setSelectorId={setPartner}
                    />

                    {noDataMessage && <NoDataFoundMessage partner={partner} />}
                </InternalGrid>

                <DashboardDataCards
                    isLoading={isLoading}
                    totalDonationAmount={
                        reportsData &&
                        formatCentsMoney(reportsData.totalDonationAmount)
                    }
                    donationCount={reportsData && reportsData.donationCount}
                    averageDonationAmount={
                        reportsData &&
                        formatCentsMoney(reportsData.averageDonationAmount)
                    }
                />

                <ErrorBoundary>
                    <DashboardCardHistogram
                        histogramData={histogramData}
                        isLoading={isLoading}
                        endDateValue={endDate}
                        startDateValue={startDate}
                        partner={partner}
                    />
                </ErrorBoundary>
            </InternalGrid>
        </>
    );
};

export default withTranslation('common')(ShopDashboard);
